// Opening the rear camera, in one place.
//
// ---------------------------------------------------------------------------
// WHY THIS EXISTS
// ---------------------------------------------------------------------------
// Two surfaces open a camera — the capture page and the scan-to-search modal —
// and they were written days apart with different constraints. The capture page
// asked for 3840x2160 and applied continuous autofocus; the modal asked for
// nothing at all and applied nothing.
//
// "Nothing" is not a neutral request. A browser with no size constraint picks
// its own default, and on Chrome that default is 640x480 — measured, not
// assumed. Blown up to fill a modern phone screen that is roughly 36 times
// fewer pixels than the capture page's stream, which is exactly why one
// scanner looked sharp and the other looked soft. It also makes DECODING
// harder: fewer pixels per barcode module means more frames before a read.
//
// So neither caller states raw constraints any more. They pick an intent, and
// the constraints for that intent live here.

/**
 * As many pixels as the device will give. For the capture page, where every
 * photograph is a frame off this stream and a label close-up is the shot whose
 * legibility depends on the resolution.
 */
export const PHOTO_QUALITY = { width: 3840, height: 2160 };

/**
 * Enough to decode comfortably and look sharp, without the cost of 4K.
 *
 * NOT the same as PHOTO_QUALITY on purpose. A scan modal is open for a few
 * seconds, nothing is kept from it, and a 4K stream costs startup latency and
 * battery for pixels nobody keeps. 1080p is far past what BarcodeDetector or
 * ZXing need and is a sharp full-screen preview on any current phone.
 */
export const SCAN_QUALITY = { width: 1920, height: 1080 };

/**
 * Ask the track for continuous autofocus.
 *
 * Capability-checked and wrapped: focusMode is not in every browser's supported
 * constraint set, and a rejected applyConstraints must never take the camera
 * down with it. A device that ignores this is no worse off than before.
 */
export async function preferContinuousFocus(track) {
  if (!track) return;
  try {
    const caps = track.getCapabilities?.() || {};
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
    }
  } catch {
    /* best-effort; the preview keeps whatever focus mode it had */
  }
}

/**
 * Open the rear camera at the given quality, with focus already asked for.
 *
 * Every size is `ideal`, never `exact`: an `exact` constraint the phone cannot
 * meet fails the whole request, and a working camera at a lower resolution
 * beats no camera at all.
 */
export async function openRearCamera(
  quality = SCAN_QUALITY,
  facing = "environment",
  mediaDevices = typeof navigator === "undefined" ? undefined : navigator.mediaDevices,
) {
  if (!mediaDevices?.getUserMedia) {
    throw Object.assign(new Error("no_media_devices"), { name: "NotSupportedError" });
  }

  const stream = await mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: facing },
      width: { ideal: quality.width },
      height: { ideal: quality.height },
    },
    audio: false,
  });

  await preferContinuousFocus(stream.getVideoTracks()[0]);
  return stream;
}

/** "1920x1080 @30fps" — for diagnosing what a device actually granted. */
export function trackSummary(track) {
  const s = track?.getSettings?.() || {};
  if (!s.width) return "unknown";
  return `${s.width}x${s.height} @${Math.round(s.frameRate || 0)}fps`;
}

const RETUNE_WAIT_MS = 2500;

/**
 * Move a live camera to another quality without a new stream: the barcode is
 * read at SCAN_QUALITY, the photographs are taken at PHOTO_QUALITY.
 *
 * A bare `ideal` never changes a running camera (Chrome scales the frames it
 * already has), so the long side carries a `min` going up, and a `max` with
 * resizeMode "none" going down: a size the current format cannot meet is what
 * makes Chrome and Safari reopen the sensor in another format, same track.
 *
 * Resolves to { w, h } once a frame at the new size has played, null if none
 * came (refused, or a camera that stalled at the new size), or undefined when
 * the camera is already there or can go no bigger.
 */
const REAL_CLOCK = { now: () => performance.now(), setTimeout: (f, ms) => setTimeout(f, ms) };

export async function retuneTrack(track, video, quality, clock = REAL_CLOCK) {
  if (!track || track.readyState !== "live" || !video) return undefined;
  const s = track.getSettings?.() || {};
  const portrait = (s.height || 0) > (s.width || 0);
  const longNow = Math.max(s.width || 0, s.height || 0);
  const raise = quality.width > longNow;
  const caps = track.getCapabilities?.() || {};
  const capLong = Math.max(caps.width?.max || 0, caps.height?.max || 0);
  const goal = raise ? Math.min(quality.width, capLong || quality.width) : quality.width;
  if (!longNow || goal === longNow || (raise && goal <= longNow)) return undefined;
  const short = Math.round((goal * quality.height) / quality.width);
  const longC = raise ? { min: goal, ideal: goal } : { ideal: goal, max: goal };
  const shortC = raise ? { ideal: short } : { ideal: short, max: short };
  const sized = portrait ? { width: shortC, height: longC } : { width: longC, height: shortC };
  try {
    await track.applyConstraints(raise ? sized : { ...sized, resizeMode: "none" });
  } catch {
    if (raise) return null;
    try {
      await track.applyConstraints(sized);
    } catch {
      return null;
    }
  }
  const there = () => {
    const l = Math.max(video.videoWidth, video.videoHeight);
    return raise ? l > longNow : l > 0 && l <= goal;
  };
  return new Promise((resolve) => {
    const t0 = clock.now();
    // A camera that stalled sends no frame callback at all.
    clock.setTimeout(() => resolve(null), RETUNE_WAIT_MS + 100);
    const check = () => {
      if (there()) resolve({ w: video.videoWidth, h: video.videoHeight });
      else if (clock.now() - t0 > RETUNE_WAIT_MS || track.readyState !== "live") resolve(null);
      else if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(check);
      else clock.setTimeout(check, 50);
    };
    check();
  });
}
